import React from 'react'

import styled from '@emotion/styled'
import {
  Button,
  ButtonColorVariant,
  FontWeight,
  Link,
  LinkVariant,
  Text,
  Box,
} from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import { useDispatch, useSelector } from 'react-redux'

import { trackCheckoutErrorBannerGetInTouch } from 'actions/tracking'
import { Alert, AlertSeverity } from 'routes/Checkout/Components/Alert/Alert'
import { checkoutConstants } from 'routes/Checkout/checkoutConstants'
import {
  getCurrentPaymentMethod,
  isApplePayPayment,
  isGooglePayPayment,
  isPayPalExpressPayment,
} from 'routes/Checkout/checkoutPaymentSelectors'
import {
  getApplePayErrors,
  getGeneralErrors,
  getGooglePayErrors,
  getPayPalErrors,
} from 'routes/Checkout/checkoutSelectors'
import { translateCheckoutErrorToMessageCode } from 'routes/Checkout/checkoutUtils'
import { PaymentMethod } from 'routes/Signup/signupConfig'

import { isSubmitting as isSubmittingSelector } from '../../utils/state'

type ErrorMessageProps = {
  onLoginClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

const ErrorMessageContainer = styled.div({
  marginBottom: '1.5rem',
})

const TextMessageContainer = styled.div({
  marginBottom: '1rem',
})

const LoginButtonContainer = styled.div({
  marginLeft: '-2rem',
})

/**
 * This component is used in Checkout to display various errors.
 * @property {() => void} onLoginClick - some errors are related to logging in issues, in this case Log in button is shown which fires this callback
 */
export const ErrorMessage = ({ onLoginClick }: ErrorMessageProps) => {
  const dispatch = useDispatch()

  const handleLoginClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    dispatch(onLoginClick?.(e))
  }

  const isSubmitting = useSelector(isSubmittingSelector)
  const currentPaymentMethod = useSelector(getCurrentPaymentMethod)
  const isApplePay = useSelector(isApplePayPayment)
  const isGooglePay = useSelector(isGooglePayPayment)
  const isPayPalExpressCheckout = useSelector(isPayPalExpressPayment)

  const applePayErrors = useSelector(getApplePayErrors) ?? Immutable.Map<string, boolean>()
  const googlePayErrors = useSelector(getGooglePayErrors) ?? Immutable.Map<string, boolean>()
  const payPalErrors = useSelector(getPayPalErrors) ?? Immutable.Map<string, boolean>()
  const generalErrors = useSelector(getGeneralErrors)

  if (isSubmitting) return null

  let entry: any[]

  switch (currentPaymentMethod) {
    case PaymentMethod.ApplePay: {
      entry = applePayErrors.merge(generalErrors).findEntry((value) => !!value)
      break
    }
    case PaymentMethod.GooglePay: {
      entry = googlePayErrors.merge(generalErrors).findEntry((value) => !!value)
      break
    }
    case PaymentMethod.PayPal: {
      entry = payPalErrors.merge(generalErrors).findEntry((value) => !!value)
      break
    }
    case PaymentMethod.PayPalExpressCheckout: {
      entry = payPalErrors.merge(generalErrors).findEntry((value) => !!value)
      break
    }
    default: {
      entry = generalErrors.findEntry((value) => !!value)
    }
  }

  if (
    !entry ||
    entry.includes('assign-order-failed') ||
    entry.includes('PAYPAL_TOKEN_ERROR_AT_CHECKOUT')
  ) {
    return null
  }

  const [errorName, errorValue] = entry
  const errorType = translateCheckoutErrorToMessageCode(
    errorName,
    errorValue,
    isApplePay,
    isGooglePay,
    isPayPalExpressCheckout,
  )

  const { errorMessage } = checkoutConstants
  const messageObject = (errorMessage as any)[errorType] || errorMessage.generic
  const { header, message, showLoginCTA, showAsSuccess, cta } = messageObject

  const severity = showAsSuccess ? AlertSeverity.Success : AlertSeverity.Error

  const handleCtaClick = () => {
    if (cta?.shouldTrackGetInTouch) {
      dispatch(trackCheckoutErrorBannerGetInTouch(errorValue, cta.route))
    }
  }

  return (
    <ErrorMessageContainer data-testing={`${errorType}`}>
      <Alert severity={severity}>
        {header && (
          <Text fontWeight={FontWeight.Bold} size={2}>
            {header}
          </Text>
        )}
        <TextMessageContainer>
          <Text size={2}>{message}</Text>
        </TextMessageContainer>
        {cta && (
          <Box>
            <Link href={cta.route} variant={LinkVariant.Text} size={2} onClick={handleCtaClick}>
              {cta.label}
            </Link>
          </Box>
        )}
        {showLoginCTA && (
          <LoginButtonContainer>
            <Button
              width="100%"
              colorVariant={ButtonColorVariant.Secondary}
              onClick={handleLoginClick}
            >
              Log in
            </Button>
          </LoginButtonContainer>
        )}
      </Alert>
    </ErrorMessageContainer>
  )
}
